import { Route, Routes } from '@angular/router';
import { buildProfileEditChildren } from '@app/features/vendor/profile/constants/profile-sections';
import { buildVendorEvaluationDetailChildren } from './vendor-evaluations/constants/vendor-evaluation-sections';

/**
 * "Detail Dokumen" vendor — `<list>/:id` DAN `<list>/:id/<slug>`.
 *
 * SATU halaman untuk Candidates & Vendors: candidate adalah record `vendor` dengan
 * `vendor_registration_state = CANDIDATE`, endpoint panelnya sama. Tidak ada di menu BE (dicapai
 * dari link kolom Nama di kedua list), jadi tak terikat `navigable.path`.
 *
 * Dipasang di DUA path (`candidates/:id` & `vendors/:id`) lewat factory ini, BUKAN satu path
 * bersama: URL-lah satu-satunya tempat halaman ini tahu dari list mana ia dibuka. Dengan itu
 * - tombol Kembali (`backLink()` di `ProfileEditComponent` = URL dikurangi satu segmen) mendarat di
 *   list asalnya, tanpa kode tambahan di shell;
 * - sidebar utama menyorot menu asalnya (prefix match `sidebar.ts`);
 * - keduanya tetap benar setelah reload / deep-link / tab baru (beda dengan `location.back()`).
 * Keputusan user 11 Sep 2026, membalik PLAN candidates §2b. Jangan disatukan lagi ke `vendors/:id`.
 * Perilaku per-state tetap dicabang dari DATA (`vendor_registration_state`), bukan dari path.
 *
 * UI-nya adalah shell "Perbarui Dokumen" milik vendor dalam mode `review`: data, tata letak,
 * dan tabelnya identik; yang berbeda hanya lapisan tombol (setujui/tolak, TANPA satu pun aksi
 * tulis). Kode tetap tinggal di `features/vendor/profile/` dan diimpor dari sini — domainnya
 * memang "profil vendor" terlepas dari siapa yang melihat
 * (plans/vendor-management/candidates/sidebar/PLAN.md D11).
 *
 * `children` dengan `path: ''` di dalamnya, BUKAN dua entry sejajar: satu entry menangani URL
 * dengan dan tanpa slug sekaligus, sehingga jebakan urutan router (entry shell yang menelan
 * URL tanpa slug lalu merender `<router-outlet />` kosong) tidak berlaku di sini sama sekali.
 *
 * Entry hasil factory HARUS tetap berada DI BAWAH route statis list-nya: router memilih entry
 * PERTAMA yang cocok, dan `<list>/:id` di atasnya akan menelan URL apa pun bersegmen dua.
 */
function vendorReviewRoute(path: 'candidates/:id' | 'vendors/:id'): Route {
  return {
    path,
    loadComponent: () =>
      import('@app/features/vendor/profile/pages/profile-edit/profile-edit').then(
        (c) => c.ProfileEditComponent,
      ),
    data: { profileEditMode: 'review', profileEditTitleKey: 'vendor.review.title' },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./vendors/pages/vendor-review-landing/vendor-review-landing').then(
            (c) => c.VendorReviewLandingComponent,
          ),
        title: 'vendor.review.title',
      },
      ...buildProfileEditChildren(),
    ],
  };
}

/**
 * Route area Vendor Management. Di-spread di `main-layout.routes.ts` TANPA segmen prefix karena
 * menu BE mengarah langsung ke `admin/applicants` — pola sama dengan User Management.
 *
 * `path` tiap entry HARUS cocok dengan `navigable.path` dari navigation_tree/MAIN (sidebar memakai
 * path itu apa adanya sebagai `routerLink`). `title` memakai key i18n (I18nTitleStrategy).
 */
export const vendorManagementRoutes: Routes = [
  {
    path: 'applicants',
    loadComponent: () =>
      import('./applicants/pages/applicants-list/applicants').then((c) => c.ApplicantsComponent),
    title: 'applicant.title',
  },
  {
    // Detail applicant — TIDAK ada di menu BE (dicapai dari link kolom Nama), jadi tak terikat
    // `navigable.path`. Sidebar tetap menyorot menu Applicants karena `isActive()` mencocokkan
    // prefix `/admin/applicants/`.
    path: 'applicants/:id',
    loadComponent: () =>
      import('./applicants/pages/applicant-detail/applicant-detail').then(
        (c) => c.ApplicantDetailComponent,
      ),
    title: 'applicant.detail.title',
  },
  {
    path: 'candidates',
    loadComponent: () =>
      import('./candidates/pages/candidates-list/candidates').then((c) => c.CandidatesComponent),
    title: 'candidate.title',
  },
  // "Detail Dokumen" dari list Candidates — lihat `vendorReviewRoute` di atas. DI BAWAH `candidates`.
  vendorReviewRoute('candidates/:id'),
  {
    path: 'vendors',
    loadComponent: () =>
      import('./vendors/pages/vendors-list/vendors').then((c) => c.VendorsComponent),
    title: 'vendor.title',
  },
  // "Detail Dokumen" dari list Vendors — lihat `vendorReviewRoute` di atas. DI BAWAH `vendors`.
  vendorReviewRoute('vendors/:id'),
  {
    // Menu Kriteria Evaluasi — accordion kelompok kriteria + kriteria di dalamnya, seluruhnya dari
    // `GET api/vendor/evaluation/criterion_groups/tree`. BUKAN halaman list CRUD: tanpa paging,
    // tanpa filter_params, tanpa `app-data-list` (plans/vendor-management/evaluation-criteria/PLAN.md).
    //
    // Path memakai UNDERSCORE karena itu `navigable.path` dari menu BE (dikonfirmasi user
    // 8 Sep 2026); jangan "dirapikan" jadi `vendor-evaluation-criteria` — sidebar memakai path BE
    // apa adanya sebagai `routerLink`. Pola sama dengan `vendor_sanction` di bawah.
    //
    // Tidak ada route detail: seluruh CRUD terjadi di modal.
    path: 'vendor_evaluation_criteria',
    loadComponent: () =>
      import('./evaluation-criteria/pages/evaluation-criteria/evaluation-criteria').then(
        (c) => c.EvaluationCriteriaComponent,
      ),
    title: 'evaluationCriterion.title',
  },
  {
    // Menu Kategori Bobot — accordion kategori bobot + item band di dalamnya, seluruhnya dari
    // `GET api/vendor/evaluation/score_cats/tree`. Kembar bentuk dengan Kriteria Evaluasi di atas;
    // keduanya berbagi cangkang kartu `<app-collapsible-group-card>` di `shared/`.
    // BUKAN halaman list CRUD (plans/vendor-management/score-categories/PLAN.md).
    //
    // ⚠️ Path menu memakai UNDERSCORE dan **tidak sama dengan nama resource API** (`score_cats`):
    // yang dipakai di sini adalah `navigable.path` dari menu BE, karena sidebar memakainya apa
    // adanya sebagai `routerLink`. Jangan "dirapikan" jadi `score-categories`, dan jangan
    // diselaraskan dengan nama resource.
    //
    // Tidak ada route detail: seluruh CRUD terjadi di modal.
    path: 'vendor_evaluation_score_categories',
    loadComponent: () =>
      import('./score-categories/pages/score-categories/score-categories').then(
        (c) => c.ScoreCategoriesComponent,
      ),
    title: 'evaluationScoreCategory.title',
  },
  {
    // Menu Evaluasi Vendor — daftar PERIODE evaluasi (`vendor_evaluation`), endpoint
    // `api/vendor/evaluation/evaluations` dipakai utuh tanpa `staticFilters`.
    //
    // Awalan endpointnya `api/vendor/evaluation/…`, sama dengan dua menu master di atas
    // (Kriteria Evaluasi & Kategori Bobot).
    //
    // Path memakai UNDERSCORE karena itu `navigable.path` dari menu BE; sidebar memakainya apa
    // adanya sebagai `routerLink`. Jangan "dirapikan" jadi `vendor-evaluations` — pola sama
    // dengan `vendor_sanction` & `vendor_statuses`.
    path: 'vendor_evaluations',
    loadComponent: () =>
      import('./vendor-evaluations/pages/vendor-evaluations-list/vendor-evaluations').then(
        (c) => c.VendorEvaluationsComponent,
      ),
    title: 'vendorEvaluation.title',
  },
  {
    // Detail evaluasi — TIDAK ada di menu BE (dicapai dari link kolom Name), jadi tak terikat
    // `navigable.path`. Entry ini HARUS tetap DI BAWAH route statis di atas: router memilih entry
    // PERTAMA yang cocok, dan `vendor_evaluations/:id` di atasnya akan menelan URL apa pun
    // bersegmen dua.
    //
    // SHELL bersidebar: komponen ini hanya kerangka (header + menu + `<router-outlet />`); isinya
    // datang dari route ANAK `vendor_evaluations/:id/<slug>`. Daftar anaknya dirakit dari
    // `VENDOR_EVALUATION_SECTION_GROUPS` — daftar yang SAMA yang merender menunya, supaya menu
    // tidak pernah bisa menunjuk rute yang tidak ada.
    //
    // Anak pertamanya adalah redirect `'' → 'overview'`, jadi URL tanpa slug mendarat di menu
    // Gambaran Umum dengan menunya bertanda aktif. Link kolom Name di
    // `vendor-evaluation.config.ts` tetap menyebut `overview` secara eksplisit supaya tidak ada
    // navigasi dua langkah.
    //
    // `GET evaluation/evaluations/:id` dimuat SEKALI oleh shell ke `VendorEvaluationDetailStore` dan
    // dibaca ketiga section (`forms` memakainya sebagai gerbang tombol "Buat Entri Baru").
    //
    // Daftar section juga membawa route ANAK non-menu (`forms/:formId`, `form_history/:vendorId`)
    // — halaman yang hanya dicapai dari sel tabel Formulir; keduanya masih "Segera hadir".
    //
    // Store di-provide di `providers` KOMPONEN shell, BUKAN di sini: berkas route ini dimuat eager,
    // jadi impor statis store dari sini menyeret store + `VendorEvaluationService` ke bundle
    // INITIAL (+1.75 kB, terukur) padahal keduanya hanya dipakai halaman yang lazy ini.
    path: 'vendor_evaluations/:id',
    loadComponent: () =>
      import('./vendor-evaluations/pages/vendor-evaluation-detail/vendor-evaluation-detail').then(
        (c) => c.VendorEvaluationDetailComponent,
      ),
    title: 'vendorEvaluation.detail.title',
    children: buildVendorEvaluationDetailChildren(),
  },
  {
    // Menu Sanction — endpoint list SAMA dengan Vendors (`api/vendor/vendors`), dipersempit ke
    // vendor AKTIF bertipe STANDARD lewat `api.staticFilters`. Path memakai UNDERSCORE karena itu
    // `navigable.path` dari menu BE; jangan "dirapikan" jadi `vendor-sanction`.
    path: 'vendor_sanction',
    loadComponent: () =>
      import('./sanctions/pages/sanctions-list/sanctions').then((c) => c.SanctionsComponent),
    title: 'sanction.title',
  },
  {
    // Detail sanksi vendor — TIDAK ada di menu BE (dicapai dari link kolom Nama), jadi tak terikat
    // `navigable.path`. Sidebar tetap menyorot menu Sanction karena `isActive()` mencocokkan prefix
    // `/admin/vendor_sanction/`. Entry ini HARUS tetap DI BAWAH route statis di atas.
    //
    // `:id` = **vendor.id**, BUKAN id record sanksi (bandingkan `vendor_statuses/:id` di bawah).
    // Halaman ini KOMPONEN TERSENDIRI, bukan alias ke `VendorDetailComponent`: isinya daftar
    // record sanksi milik satu vendor + form tambah sanksi, bukan profil vendor. Rencana lama
    // untuk mengalihkannya ke `VendorDetailComponent` sudah BATAL — lihat
    // plans/vendor-management/vendors/PLAN-sanction-detail.md.
    path: 'vendor_sanction/vendor/:id',
    loadComponent: () =>
      import('./sanctions/pages/sanction-vendor-detail/sanction-vendor-detail').then(
        (c) => c.SanctionVendorDetailComponent,
      ),
    title: 'sanction.detail.title',
  },
  {
    // Menu Sanction List — daftar RECORD sanksi (`vendor_status`), BEDA dari menu Sanction di atas
    // yang memuat daftar VENDOR. Endpoint & folder fiturnya terpisah.
    path: 'vendor_statuses',
    loadComponent: () =>
      import('./vendor-statuses/pages/vendor-statuses-list/vendor-statuses').then(
        (c) => c.VendorStatusesComponent,
      ),
    title: 'vendorStatus.title',
  },
  {
    // Detail record sanksi — TIDAK ada di menu BE (dicapai dari link kolom Nama Vendor), jadi tak
    // terikat `navigable.path`. Entry ini HARUS tetap DI BAWAH route statis di atas.
    path: 'vendor_statuses/:id',
    loadComponent: () =>
      import('./vendor-statuses/pages/vendor-status-detail/vendor-status-detail').then(
        (c) => c.VendorStatusDetailComponent,
      ),
    title: 'vendorStatus.detail.title',
  },
];
